/**
 * Mega-menu search service — data-fetching contract for the search overlay.
 *
 * The component calls these functions directly — no props threading needed.
 */

// ** Types
import type { SearchResult, SearchSuggestion } from '@/components/common/MegaMenuOverlay/megaMenuOverlay.types';
import type { SearchOption } from '@/components/common/searchSection/types';

// ** Mocks
import {
	AP07_MEGA_MENU_SEARCH_FILTER_THEMA_OPTIONS,
	AP07_MEGA_MENU_SEARCH_FILTER_TYP_OPTIONS,
	AP07_MEGA_MENU_SEARCH_RESULTS,
	AP07_MEGA_MENU_SEARCH_SUGGESTIONS
} from '../mocks/ap07.search.mock';

// ── Types ───────────────────────────────────────────────────────────────────

export type MegaMenuSearchFilters = {
	typ?: string;
	thema?: string;
};

export type MegaMenuSearchFilterOptions = {
	typOptions: SearchOption[];
	themaOptions: SearchOption[];
};

// ── Service functions ───────────────────────────────────────────────────────

/**
 * Fetch autocomplete suggestions while the user types in the mega-menu search.
 *
 * Called after a 300 ms debounce (handled by the component).
 * Return an empty array to hide the suggestion dropdown.
 *
 * @param query   Current input value (already trimmed, ≥ 2 chars).
 * @param signal  Passed by the component so in-flight requests can be aborted
 *                when the user types again or leaves the overlay.
 *
 * **TODO(strapi):** Replace with a GraphQL query, e.g.:
 */
export async function fetchMegaMenuSuggestions(query: string, _signal?: AbortSignal): Promise<SearchSuggestion[]> {
	// ── Mock: client-side filter on static data ─────────────────────────────
	return AP07_MEGA_MENU_SEARCH_SUGGESTIONS.filter((s) => s.label.toLowerCase().includes(query.toLowerCase()));
}

/**
 * Execute a full-text search and return result cards.
 *
 * The component shows a loading spinner immediately, then renders cards or
 * the empty-state based on the length of the returned array.
 *
 * @param query    Search term (already trimmed, non-empty).
 * @param filters  Currently selected dropdown values. `undefined` = "all".
 * @param signal   AbortSignal for cancellation.
 *
 * **TODO(strapi):** Replace with a GraphQL query, e.g.:
 */
export async function fetchMegaMenuResults(
	query: string,
	_filters?: MegaMenuSearchFilters,
	_signal?: AbortSignal
): Promise<SearchResult[]> {
	// ── Mock: 800 ms latency, only "strat*" queries return results ──────────
	await new Promise((resolve) => setTimeout(resolve, 800));
	const hasResults = query.toLowerCase().includes('strat');
	return hasResults ? AP07_MEGA_MENU_SEARCH_RESULTS : [];
}

/**
 * Fetch the available filter options for the "Typ" and "Thema" dropdowns.
 * **TODO(strapi):** Replace with a GraphQL query or keep as static CMS data.
 */
export async function fetchMegaMenuSearchFilterOptions(): Promise<MegaMenuSearchFilterOptions> {
	// ── Mock: static options ────────────────────────────────────────────────
	return {
		typOptions: AP07_MEGA_MENU_SEARCH_FILTER_TYP_OPTIONS,
		themaOptions: AP07_MEGA_MENU_SEARCH_FILTER_THEMA_OPTIONS
	};
}
